import * as THREE from "three";
import type { ThemeId, TimeOfDay } from "@shared/types";
import { detectQuality, getQualitySettings, type QualitySettings } from "./quality";

export interface ThemeColors {
  id: ThemeId;
  /** Sky color at the top of the dome. */
  sky: THREE.Color;
  /** Sky color near the horizon — used for the gradient. */
  skyHorizon: THREE.Color;
  fog: THREE.Color;
  ground: THREE.Color;
  pathLight: THREE.Color;
  pathDark: THREE.Color;
  ambient: number;
  sun: number;
  hemiGround: THREE.Color;
  /** Whether to render a visible sun mesh (skip at night). */
  showSun: boolean;
}

/* ── Palette: bright, saturated, kid-friendly. ────────────────────── */
export function themeFor(theme: ThemeId, time: TimeOfDay): ThemeColors {
  const palettes: Record<ThemeId, Omit<ThemeColors, "id" | "ambient" | "sun" | "showSun">> = {
    forest: {
      sky: new THREE.Color("#62b9ff"),
      skyHorizon: new THREE.Color("#cdebff"),
      fog: new THREE.Color("#cdebff"),
      ground: new THREE.Color("#3da548"),
      pathLight: new THREE.Color("#e6c898"),
      pathDark: new THREE.Color("#b88965"),
      hemiGround: new THREE.Color("#4faa45"),
    },
    snow: {
      sky: new THREE.Color("#9bd1ff"),
      skyHorizon: new THREE.Color("#e9f5ff"),
      fog: new THREE.Color("#eaf3fb"),
      ground: new THREE.Color("#f0f5fa"),
      pathLight: new THREE.Color("#e2eaf2"),
      pathDark: new THREE.Color("#a8b4c0"),
      hemiGround: new THREE.Color("#e8eff6"),
    },
    desert: {
      sky: new THREE.Color("#ffc382"),
      skyHorizon: new THREE.Color("#ffe4b8"),
      fog: new THREE.Color("#ffd9a8"),
      ground: new THREE.Color("#e6a86a"),
      pathLight: new THREE.Color("#f0c896"),
      pathDark: new THREE.Color("#b88660"),
      hemiGround: new THREE.Color("#dd965e"),
    },
    space: {
      sky: new THREE.Color("#0a1140"),
      skyHorizon: new THREE.Color("#3b3f7e"),
      fog: new THREE.Color("#1a1e4a"),
      ground: new THREE.Color("#2c2c5a"),
      pathLight: new THREE.Color("#4a4a7a"),
      pathDark: new THREE.Color("#2a2a4a"),
      hemiGround: new THREE.Color("#2a2c50"),
    },
    jungle: {
      sky: new THREE.Color("#7fc870"),
      skyHorizon: new THREE.Color("#d0eab2"),
      fog: new THREE.Color("#cce8b8"),
      ground: new THREE.Color("#2a8038"),
      pathLight: new THREE.Color("#a06840"),
      pathDark: new THREE.Color("#724a28"),
      hemiGround: new THREE.Color("#3d943f"),
    },
    candy: {
      sky: new THREE.Color("#ffaad8"),
      skyHorizon: new THREE.Color("#ffe4f2"),
      fog: new THREE.Color("#ffd4ec"),
      ground: new THREE.Color("#ff9bcc"),
      pathLight: new THREE.Color("#fff0f7"),
      pathDark: new THREE.Color("#ff8bc4"),
      hemiGround: new THREE.Color("#ffabd6"),
    },
  };
  const colors = palettes[theme];
  // Brightened across the board — kid-friendly bright lighting.
  const lightByTime: Record<TimeOfDay, [number, number]> = {
    auto: [1.0, 1.7],
    morning: [1.1, 1.8],
    afternoon: [0.95, 1.85],
    sunset: [0.85, 1.4],
    night: [0.55, 0.85],
  };
  const [ambient, sun] = lightByTime[time];
  return { id: theme, ...colors, ambient, sun, showSun: time !== "night" };
}

export interface SceneOptions {
  qualityOverride?: QualitySettings;
  skipShadows?: boolean;
}

/** Cached so we don't allocate a new canvas for every page. */
const _skyCache = new Map<string, THREE.CanvasTexture>();
function makeSkyTexture(top: THREE.Color, bottom: THREE.Color): THREE.CanvasTexture {
  const key = `${top.getHexString()}-${bottom.getHexString()}`;
  const cached = _skyCache.get(key);
  if (cached) return cached;
  const c = document.createElement("canvas");
  c.width = 4;
  c.height = 512;
  const ctx = c.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, 0, 512);
  grad.addColorStop(0, "#" + top.getHexString());
  grad.addColorStop(1, "#" + bottom.getHexString());
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 4, 512);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  _skyCache.set(key, tex);
  return tex;
}

/** Big bright sun with a soft halo. Lives in scene-space; we move it each
 *  frame to stay at a fixed offset from the camera (skybox-style). */
function buildSun(): THREE.Group {
  const g = new THREE.Group();
  // Core: warm bright disc.
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(6, 18, 14),
    new THREE.MeshBasicMaterial({ color: 0xfff2b0, fog: false }),
  );
  g.add(core);
  // Halo: larger transparent sphere — gives a soft glow without post-processing.
  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(11, 18, 14),
    new THREE.MeshBasicMaterial({
      color: 0xfff2b0,
      transparent: true,
      opacity: 0.22,
      fog: false,
    }),
  );
  g.add(halo);
  const halo2 = new THREE.Mesh(
    new THREE.SphereGeometry(18, 18, 14),
    new THREE.MeshBasicMaterial({
      color: 0xfff2b0,
      transparent: true,
      opacity: 0.08,
      fog: false,
    }),
  );
  g.add(halo2);
  return g;
}

export class GameScene {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  sun: THREE.DirectionalLight;
  ambient: THREE.AmbientLight;
  hemi: THREE.HemisphereLight;
  quality: QualitySettings;
  /** Visible sun mesh; null for night themes. */
  private sunMesh: THREE.Group | null = null;

  constructor(canvas: HTMLCanvasElement, theme: ThemeColors, opts: SceneOptions = {}) {
    this.quality = opts.qualityOverride ?? getQualitySettings(detectQuality());
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: this.quality.antialias,
      powerPreference: "high-performance",
      stencil: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatioCap));

    if (!opts.skipShadows) {
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = this.quality.softShadows
        ? THREE.PCFSoftShadowMap
        : THREE.BasicShadowMap;
    } else {
      this.renderer.shadowMap.enabled = false;
    }
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // Brighter exposure across the board.
    this.renderer.toneMappingExposure = 1.25;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Sky gradient (top → horizon).
    this.scene.background = makeSkyTexture(theme.sky, theme.skyHorizon);
    // Push fog out so the bright sky isn't washed by haze near the horizon.
    this.scene.fog = new THREE.Fog(theme.fog, 60, 200);

    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 350);
    this.camera.position.set(0, 6.5, -10);
    this.camera.lookAt(0, 1.5, 12);

    // Hemisphere light: warm sky → cool ground tint, fills shadows naturally.
    this.hemi = new THREE.HemisphereLight(theme.sky, theme.hemiGround, this.quality.hemiLightIntensity * 1.15);
    this.scene.add(this.hemi);

    // Ambient bumped a touch to match the brighter theme.
    this.ambient = new THREE.AmbientLight(0xffffff, theme.ambient * 0.45);
    this.scene.add(this.ambient);

    // Warm sun directional light + visible sun mesh (skipped at night).
    this.sun = new THREE.DirectionalLight(0xfff1c8, theme.sun);
    this.sun.position.set(15, 35, 10);
    if (!opts.skipShadows) {
      this.sun.castShadow = true;
      this.sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
      const d = 22;
      this.sun.shadow.camera.left = -d;
      this.sun.shadow.camera.right = d;
      this.sun.shadow.camera.top = d;
      this.sun.shadow.camera.bottom = -d;
      this.sun.shadow.camera.near = 0.5;
      this.sun.shadow.camera.far = 90;
      this.sun.shadow.bias = -0.0008;
      this.sun.shadow.normalBias = 0.04;
    }
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    if (theme.showSun) {
      this.sunMesh = buildSun();
      this.scene.add(this.sunMesh);
    }
  }

  followShadows(playerX: number, playerZ: number) {
    if (this.sun.castShadow) {
      this.sun.target.position.set(playerX, 0, playerZ + 6);
      this.sun.position.set(playerX + 15, 35, playerZ + 16);
      this.sun.target.updateMatrixWorld();
    }
    // Park the sun mesh at a fixed offset from the camera so it stays in the
    // sky regardless of how far down the path the player has run.
    if (this.sunMesh) {
      this.sunMesh.position.set(playerX + 50, 70, playerZ + 110);
    }
  }

  resize(w: number, h: number) {
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.renderer.dispose();
    this.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mat = obj.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat.dispose();
      }
    });
  }
}
